import { useEffect, useRef, useState } from 'react'

const cache: Record<string, Promise<void> | undefined> = {}

interface UseLazyLoadOption {
  onLoad?: () => void
  onError?: (error: string | Event) => void
  skip?: boolean
}

/**
 * 스크립트 lazy loader 입니다.
 * @param src 외부 script 주소
 * @param onLoad 스크립트 로드 완료시 콜백
 * @returns An object with the following properties:
 * - `loading` (boolean): 스크립트가 로드되었는지 여부를 나타냅니다.
 * - `error` (string|Event|undefined): 스크립트 로드 중 발생한 오류를 나타냅니다.
 */
const useLazyLoadScript = (src: string, option: UseLazyLoadOption = {}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | Event | undefined>(undefined)

  const onLoadRef = useRef<() => void>()
  onLoadRef.current = option.onLoad

  const onErrorRef = useRef<UseLazyLoadOption['onError']>()
  onErrorRef.current = option.onError

  const skipRef = useRef<boolean>()
  skipRef.current = option.skip ?? false

  useEffect(() => {
    if (skipRef.current) return

    const handleComplete = () => {
      setLoading(false)
      onLoadRef.current?.()
    }

    const handleError: UseLazyLoadOption['onError'] = error => {
      setLoading(false)
      setError(error)
      onErrorRef.current?.(error)
    }

    if (cache[src]) {
      cache[src]!.then(handleComplete).catch(handleError)
      return
    }

    cache[src] = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = () => {
        handleComplete()
        resolve()
      }
      script.onerror = error => {
        reject(error)
        handleError(error)
      }
      document.body.appendChild(script)
    })
  }, [src])

  return { loading, error }
}

export default useLazyLoadScript
